import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, AUTH_EXPIRED_EVENT, setAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=guest, obj=authed
  const [loading, setLoading] = useState(true);

  // Reload just the current user (used after profile/MFA changes). Assumes we
  // already hold a valid in-memory access token.
  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
    } catch {
      setUser(false);
    }
  }, []);

  // On page load the in-memory access token is empty, so exchange the httpOnly
  // refresh cookie for a fresh access token, then load the user. A 401 here
  // just means "not signed in".
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.post("/auth/refresh");
        setAccessToken(data.access_token);
        const me = await api.get("/auth/me");
        if (alive) setUser(me.data.user);
      } catch {
        setAccessToken(null);
        if (alive) setUser(false);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []);

  // When a mid-session request can't be refreshed, drop to guest so the route
  // guards send the user to /login rather than leaving pages stuck loading.
  useEffect(() => {
    const onExpired = () => { setAccessToken(null); setUser(false); setLoading(false); };
    window.addEventListener(AUTH_EXPIRED_EVENT, onExpired);
    return () => window.removeEventListener(AUTH_EXPIRED_EVENT, onExpired);
  }, []);

  const login = async (email, password, totp_code) => {
    const { data } = await api.post("/auth/login", { email, password, totp_code });
    if (data.mfa_required) return { mfa_required: true };
    setAccessToken(data.access_token);
    setUser(data.user);
    return { user: data.user };
  };
  const register = async (email, password, name, company) => {
    const { data } = await api.post("/auth/register", { email, password, name, company });
    setAccessToken(data.access_token);
    setUser(data.user);
    return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setAccessToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

export const isStaff = (role) => ["ops", "finance", "admin", "super_admin"].includes(role);
export const isAdmin = (role) => ["admin", "super_admin"].includes(role);
